import { useMemo, useState } from "react";
import { Badge } from "../ui/Badge";
import ShowNoteDetails from "./ShowNoteDetails";
import VisitFeedbackModal, {
  CodeWithRationale,
  OverrideRationaleModal,
  RatingWithComment,
} from "./VisitFeedbackModal";
import {
  formatCodeOrDash,
  getEandMComparison,
  getPatientStarRating,
} from "./patientVisitFields";

export default function ShowPatientDetails({ patients, onClose, doctorName }) {
  const [activePatient, setActivePatient] = useState(null);
  const [openNoteModal, setOpenNoteModal] = useState(false);
  const [feedbackPatient, setFeedbackPatient] = useState(null);
  const [rationalePatient, setRationalePatient] = useState(null);
  const [showStatusTooltip, setShowStatusTooltip] = useState(false);

  const sortedPatients = useMemo(() => {
    return [...(patients || [])].sort((a, b) =>
      String(b.status || "").localeCompare(String(a.status || ""))
    );
  }, [patients]);

  const statusCounts = sortedPatients.reduce((acc, patient) => {
    acc[patient.status] = (acc[patient.status] || 0) + 1;
    return acc;
  }, {});

  const openNote = (patient) => {
    setActivePatient(patient);
    setOpenNoteModal(true);
  };

  const closeNote = () => {
    setActivePatient(null);
    setOpenNoteModal(false);
  };

  if (sortedPatients.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-[80%] max-w-3xl">
          <div className="flex justify-between items-center mb-4">
            <div className="flex flex-col">
              <h2 className="text-2xl font-bold text-ktx-navy">No Patients Found for {doctorName} today</h2>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-ktx-navy">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[90%] max-w-6xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-ktx-navy">Patient Details for {doctorName}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-ktx-navy"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-knowtex-shadow">
            <div className="text-sm text-gray-500">Recorded and Uploaded</div>
            <div className="text-2xl font-bold text-green-600">
              {statusCounts["Locked"] || 0}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-knowtex-shadow">
            <div className="text-sm text-gray-500">Notes to Upload to EMR</div>
            <div className="text-2xl font-bold text-yellow-600">
              {statusCounts["Review"] || 0}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-knowtex-shadow">
            <div className="text-sm text-gray-500">Not recorded</div>
            <div className="text-2xl font-bold text-red-600">
              {statusCounts["Ready to Record"] || 0}
            </div>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto rounded-md shadow-knowtex-shadow">
          <table className="w-full">
            <thead className="sticky top-0 bg-white">
              <tr className="bg-ktx-purple text-white">
                <th className="px-4 py-2 text-left">Description</th>
                <th className="px-4 py-2 text-left">MRN</th>
                <th className="px-4 py-2 text-center">
                  <div className="flex items-center justify-center gap-1" onMouseLeave={() => setShowStatusTooltip(false)}>
                    Status
                    <div className="relative group">
                      <svg
                        className="w-5 h-5 text-white hover:text-yellow-400 transition-colors duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        onMouseEnter={() => setShowStatusTooltip(true)}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm0-14v4m0 4h.01"
                        />
                      </svg>
                      {showStatusTooltip && (
                        <span className="absolute top-0 left-[-2rem] -translate-x-1/2 min-h-[2rem] flex items-center px-3 bg-sage-green text-white text-sm rounded-lg transition-opacity whitespace-nowrap">
                          Click on the status to view note details
                        </span>
                      )}
                    </div>
                  </div>
                </th>
                <th className="px-4 py-2 text-center">Rating</th>
                <th className="px-4 py-2 text-center">E&M selected?</th>
                <th className="px-4 py-2 text-center">Final E&M</th>
                <th className="px-4 py-2 text-center">Knowtex E&M</th>
              </tr>
            </thead>
            <tbody>
              {sortedPatients.map((patient, index) => {
                const eAndM = getEandMComparison(patient);
                const { rating, additionalFeedback } = getPatientStarRating(patient.user_feedback);
                return (
                  <tr
                    key={patient.patient_id || `${patient.mrn}-${index}`}
                    className="hover:bg-gray-50 border-b cursor-pointer"
                    onClick={() => openNote(patient)}
                  >
                    <td className="px-4 py-2">{patient.description}</td>
                    <td className="px-4 py-2">{patient.mrn}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`
                      inline-block px-2 py-1 rounded-full text-sm
                      ${patient.status === "Locked"
                          ? "bg-green-100 text-green-800"
                          : patient.status === "Review"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }
                      `}>
                        {patient.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <RatingWithComment
                        rating={rating}
                        additionalFeedback={additionalFeedback}
                        onOpenComment={() => setFeedbackPatient(patient)}
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <Badge
                        variant="outline"
                        className={eAndM.providerSelected
                          ? "border-purple-200 bg-purple-50 text-ktx-purple"
                          : "border-gray-200 bg-gray-50 text-gray-600"}
                      >
                        {eAndM.providerSelected ? "Yes" : "No"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <CodeWithRationale
                        code={eAndM.finalCode}
                        rationale={eAndM.overrideRationale}
                        onOpenRationale={() => setRationalePatient(patient)}
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      {formatCodeOrDash(eAndM.knowtexCode)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {openNoteModal && (
        <ShowNoteDetails patientRecord={activePatient} closeNote={closeNote} />
      )}
      <VisitFeedbackModal
        open={Boolean(feedbackPatient)}
        onOpenChange={(open) => {
          if (!open) setFeedbackPatient(null);
        }}
        patientName={feedbackPatient?.description}
        mrn={feedbackPatient?.mrn}
        {...(feedbackPatient
          ? getPatientStarRating(feedbackPatient.user_feedback)
          : { rating: null, additionalFeedback: null })}
      />
      <OverrideRationaleModal
        open={Boolean(rationalePatient)}
        onOpenChange={(open) => {
          if (!open) setRationalePatient(null);
        }}
        patientName={rationalePatient?.description}
        mrn={rationalePatient?.mrn}
        {...(rationalePatient
          ? getEandMComparison(rationalePatient)
          : { finalCode: null, knowtexCode: null, overrideRationale: null })}
      />
    </div>
  );
}
